import { motion } from "motion/react";
import { Card, CardContent } from "../ui/card";
import { ShoppingCart, Upload, ClipboardList, Scan } from "lucide-react";
import { Button } from "../ui/button";
import { useNavigate } from "react-router";

interface QuickActionProps {
  icon: React.ElementType;
  title: string;
  description: string;
  color: string;
  onClick: () => void;
}

function QuickActionCard({
  icon: Icon,
  title,
  description,
  color,
  onClick,
}: QuickActionProps) {
  return (
    <motion.div
      whileHover={{ scale: 1.02, y: -4 }}
      whileTap={{ scale: 0.98 }}
      className="cursor-pointer"
      onClick={onClick}
    >
      <Card className="border-0 shadow-sm hover:shadow-md transition-all duration-200 h-full">
        <CardContent className="p-6">
          <div className="flex flex-col items-center text-center gap-3">
            <motion.div
              whileHover={{ rotate: 5, scale: 1.1 }}
              className={`w-16 h-16 rounded-2xl flex items-center justify-center ${color}`}
            >
              <Icon className="w-8 h-8 text-white" />
            </motion.div>
            <div>
              <h3 className="font-semibold text-foreground mb-1">{title}</h3>
              <p className="text-sm text-muted-foreground">{description}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

export function QuickActions() {
  const navigate = useNavigate();

  const actions = [
    {
      icon: ShoppingCart,
      title: "Start New Sale",
      description: "Process customer checkout",
      color: "bg-emerald-600",
      onClick: () => navigate("/pos"),
    },
    {
      icon: Upload,
      title: "Upload Invoice",
      description: "Add supplier invoice",
      color: "bg-blue-600",
      onClick: () => navigate("/inventory/invoice"),
    },
    {
      icon: ClipboardList,
      title: "Create Purchase Order",
      description: "Order from suppliers",
      color: "bg-purple-600",
      onClick: () => navigate("/purchase-orders"),
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.5 }}
      className="grid grid-cols-1 md:grid-cols-3 gap-4"
    >
      {actions.map((action, index) => (
        <motion.div
          key={action.title}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.6 + index * 0.1 }}
        >
          <QuickActionCard {...action} />
        </motion.div>
      ))}
    </motion.div>
  );
}
